import React, { useState, useRef, useMemo, useEffect } from 'react';
import { Box, Typography } from "@mui/joy";
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { RouteComponentProps } from '../../models/RouteComponent';
import { getChecklistTemplate, deleteChecklistTemplate, customizeChecklistTemplate, duplicateChecklistTemplate } from '../../service/checklistService';
import ChecklistTemplateCreateDialog from './ChecklistTemplateCreateDialog';
import ChecklistTemplateViewDialog from './ChecklistTemplateViewDialog';
import GenericTableView from '../../components/GenericTableView';
import { GenericTableColumTypes, GenericTableFilterTypes } from '../../enum/TableView';
import { GenericTableColumns } from '../../models/GenericTableViewModel';
import { FilterMatchMode } from 'primereact/api';
import { MenuItem } from 'primereact/menuitem';
import { Tag } from 'primereact/tag';

interface ChecklistTemplateTableProps {
  props: RouteComponentProps;
}

export default function ChecklistTemplateTable({ props }: ChecklistTemplateTableProps) {
  const toast = useRef<Toast>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [viewDialogVisible, setViewDialogVisible] = useState(false);
  const [templateToView, setTemplateToView] = useState<any>(null);
  const [reload, setReload] = useState(false);

  // Determine if we're in a client context (Super Admin viewing client templates)
  const isClientContext = useMemo((): boolean => {
    const pathCheck = window.location.pathname.includes('/client/');
    const sessionCheck = props.session?.client_id && props.session?.active_type === 'Admin';
    
    // For DSF users, check if there's a client_id available (indicating client context)
    const dsfClientCheck = props.session?.user_type === 'DSF' && props.session?.client_id;
    
    const result = Boolean(pathCheck || sessionCheck || dsfClientCheck);
    
    console.log('Client context detection:', {
      pathname: window.location.pathname,
      pathCheck,
      sessionCheck,
      dsfClientCheck,
      session: props.session,
      sessionClientId: props.session?.client_id,
      sessionActiveType: props.session?.active_type,
      sessionUserType: props.session?.user_type,
      result
    });
    
    return result;
  }, [props.session]);

  // Extract client ID from URL or session for API calls
  const clientId = useMemo(() => {
    let result = null;
    
    if (window.location.pathname.includes('/client/')) {
      const pathParts = window.location.pathname.split('/');
      const clientIndex = pathParts.indexOf('client');
      result = pathParts[clientIndex + 1];
    } else {
      // Check if there's a client_id in session (for Super Admin in client context)
      result = props.session?.client_id;
    }
    
    console.log('Client ID extraction:', {
      pathname: window.location.pathname,
      sessionClientId: props.session?.client_id,
      extractedClientId: result
    });
    
    return result;
  }, [props.session?.client_id]);

  // Determine user permissions
  const isActualClientUser = props.session?.user_type === 'Client';
  const isSuperAdmin = props.session?.user_type === 'DSF' || 
                      props.session?.user_type === 'Admin';

  // When in client context (even as Super Admin), show client-appropriate restrictions
  const shouldShowClientView = isClientContext || isActualClientUser;
  
  // Determine capabilities based on context and user type
  const canCreateTemplates = isSuperAdmin && !isClientContext; // Only in root context

  // Update base filter to include client context if needed
  const baseFilter = useMemo(() => {
    const filter = {
      'name': { value: '', matchMode: FilterMatchMode.CONTAINS },
      'appointment_name': { value: '', matchMode: FilterMatchMode.CONTAINS },
      'trigger_type': { value: '', matchMode: FilterMatchMode.EQUALS },
      'active': { value: null, matchMode: FilterMatchMode.EQUALS }
    };
    return filter;
  }, []);

  // Update GenericTableView URL to include client context
  const tableUrl = useMemo(() => {
    if (isClientContext && clientId) {
      const url = `checklist-template?clientId=${clientId}`;
      console.log('Using client context URL:', url, { isClientContext, clientId });
      return url;
    }
    console.log('Using root context URL:', 'checklist-template');
    return 'checklist-template';
  }, [isClientContext, clientId]);

  // Debug logging for view dialog state changes
  useEffect(() => {
    console.log('View dialog state changed:', { 
      viewDialogVisible, 
      hasTemplate: !!templateToView,
      templateName: templateToView?.name,
      templateId: templateToView?.id
    });
  }, [viewDialogVisible, templateToView]);

  const frequencyOptions = [
    { label: 'Hourly', value: 'hourly' },
    { label: 'Daily', value: 'daily' },
    { label: 'Weekly', value: 'weekly' },
    { label: 'Monthly', value: 'monthly' },
    { label: 'Quarterly', value: 'quarterly' },
    { label: 'Annually', value: 'annually' },
    { label: 'Custom', value: 'custom' }
  ];

  const triggerOptions = [
    // { label: 'DSTI', value: 'dsti' }, // Hidden while feature is being developed
    { label: 'Frequency', value: 'frequency' }
  ];

  const getFrequencyLabel = (type: string, intervalDays?: number) => {
    const frequencyLabel = frequencyOptions.find(opt => opt.value === type)?.label || type;
    return type === 'custom' && intervalDays ? `${frequencyLabel} (${intervalDays} days)` : frequencyLabel;
  };

  const getTriggerLabel = (triggerType: string, frequencyType?: string, intervalDays?: number) => {
    if (triggerType === 'dsti') {
      return 'DSTI';
    } else {
      return getFrequencyLabel(frequencyType || '', intervalDays);
    }
  };

  const columns: GenericTableColumns[] = [
    {
      field: 'name',
      header: 'Template Name',
      type: GenericTableColumTypes.Text,
      visible: true,
      sort: true,
      filter: true,
      filterOption: {
        type: GenericTableFilterTypes.Text,
        placeholder: 'Search by name'
      }
    },
    {
      field: 'appointment_name',
      header: 'Appointment',
      type: GenericTableColumTypes.Text,
      visible: true,
      sort: true,
      filter: true,
      filterOption: {
        type: GenericTableFilterTypes.Text,
        placeholder: 'Search by appointment'
      }
    },
    {
      field: 'trigger_type',
      header: 'Trigger',
      type: GenericTableColumTypes.Template,
      visible: true,
      sort: true,
      filter: true,
      filterOption: {
        type: GenericTableFilterTypes.Select,
        options: triggerOptions
      },
      template: (rowData: any) => getTriggerLabel(rowData.trigger_type, rowData.frequency_type, rowData.frequency_interval_days)
    },
    {
      field: 'structure',
      header: 'Structure',
      type: GenericTableColumTypes.Template,
      visible: true,
      sort: false,
      filter: false,
      template: (rowData: any) => {
        const categoryCount = parseInt(rowData.category_count) || 0;
        const questionCount = parseInt(rowData.question_count) || 0;
        return (
          <div>
            <div>{categoryCount} categories</div>
            <small className="text-muted">{questionCount} questions</small>
          </div>
        );
      }
    },
    {
      field: 'conditions_summary',
      header: 'Conditions',
      type: GenericTableColumTypes.Template,
      visible: true,
      sort: false,
      filter: false,
      template: (rowData: any) => (
        <span className="text-truncate" style={{ maxWidth: '200px', display: 'block' }}>
          {rowData.conditions_summary || 'No conditions'}
        </span>
      )
    },
    {
      field: 'template_status',
      header: isClientContext ? 'Status' : 'Type',
      type: GenericTableColumTypes.Template,
      visible: true,
      sort: false,
      filter: false,
      template: (rowData: any) => {
        if (isClientContext) {
          if (rowData.template_type === 'customized') {
            return <Tag value="Customized" severity="info" />;
          } else if (rowData.is_customized) {
            return <Tag value="Already Customized" severity="warning" />;
          } else {
            return <Tag value="Available to Customize" severity="success" />;
          }
        } else {
          return <Tag value="Master Template" severity="info" />;
        }
      }
    },
    {
      field: 'active',
      header: 'Active',
      type: GenericTableColumTypes.Template,
      visible: true,
      sort: true,
      filter: false,
      template: (rowData: any) => (
        <Tag
          value={rowData.active ? 'Yes' : 'No'}
          severity={rowData.active ? 'success' : 'danger'}
        />
      )
    },
    {
      field: 'created_at',
      header: 'Created',
      type: GenericTableColumTypes.Date,
      visible: true,
      sort: true,
      filter: false
    }
  ];

  const addButton = () => {
    setSelectedTemplate(null);
    setDialogVisible(true);
  };

  const [isViewLoading, setIsViewLoading] = useState(false);

  const itemSelected = (row: any) => {
    // This is called when a row is clicked - we can use this for viewing
    console.log('Item selected - full row data:', { row, rowValue: row?.value, rowType: typeof row?.value });
    
    // Check if row.value is null/undefined, which can happen after table reloads
    // when the selection state gets out of sync with the data
    if (!row || row.value === null || row.value === undefined) {
      console.error('Row data is null/undefined - likely selection state corruption:', { row });
      
      // Show user-friendly message explaining the template was deselected
      toast.current?.show({
        severity: 'info',
        summary: 'Template Deselected',
        detail: 'Template deselected. Please click the template again if you would like to open the same template.'
      });
      return;
    }
    
    const template = row.value;
    console.log('Item selected for viewing:', { template, hasId: !!template?.id });
    
    if (!template.id) {
      console.error('Template missing ID:', template);
      toast.current?.show({
        severity: 'warn',
        summary: 'Selection Error', 
        detail: 'Invalid template data - please refresh the page'
      });
      return;
    }
    
    // Prevent rapid double-clicks while loading
    if (isViewLoading) {
      console.log('View already loading, ignoring click');
      return;
    }
    
    viewTemplate(template);
  };

  const contextItemSelected = (row: any) => {
    console.log('Context item selected:', { row, rowValue: row?.value });
    
    if (!row?.value) {
      console.error('Invalid row data for context selection:', row);
      return;
    }
    
    setSelectedTemplate(row.value);
  };

  const viewTemplate = async (template: any) => {
    console.log('viewTemplate called with:', { template, hasId: !!template?.id, isLoading: isViewLoading });
    
    if (!template?.id) {
      console.error('Invalid template in viewTemplate:', template);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Invalid template selected'
      });
      return;
    }

    // Prevent multiple simultaneous requests
    if (isViewLoading) {
      console.log('Already loading a template view, ignoring request');
      return;
    }

    setIsViewLoading(true);
    try {
      console.log('=== VIEW TEMPLATE START ===');
      console.log('Step 1: Calling getChecklistTemplate with ID:', template.id);
      
      const templateData = await getChecklistTemplate(template.id);
      
      console.log('Step 2: API Response received:', JSON.stringify(templateData, null, 2));
      console.log('Step 3: templateData type:', typeof templateData);
      console.log('Step 4: templateData is null?', templateData === null);
      console.log('Step 5: templateData is undefined?', templateData === undefined);
      console.log('Step 6: templateData keys:', templateData ? Object.keys(templateData) : []);
      
      const hasTemplateData = !!templateData;
      const hasId = !!templateData?.id;
      
      console.log('Step 7: Validation check:', { 
        hasTemplateData, 
        hasId,
        willFail: !templateData || !templateData.id
      });
      
      if (!templateData || !templateData.id) {
        console.error('=== VALIDATION FAILED ===');
        console.error('Full response:', templateData);
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Template data is incomplete or invalid'
        });
        return;
      }
      
      console.log('Step 8: Validation passed! Setting template:', templateData.name);
      
      // Set both state values - React 18 will batch these automatically
      setTemplateToView(templateData);
      setViewDialogVisible(true);
      
      console.log('Step 9: State updated. Dialog should be visible.');
      console.log('=== VIEW TEMPLATE END ===');
    } catch (error) {
      console.error('Error fetching template details for viewing:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load template details'
      });
    } finally {
      setIsViewLoading(false);
    }
  };

  const customizeTemplate = async (template: any) => {
    try {
      if (!clientId) {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Client ID not found',
          life: 3000
        });
        return;
      }

      props.setGlobalLoader(true);
      
      await customizeChecklistTemplate(template.id, clientId);
      
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Template customized successfully',
        life: 3000
      });
      
      setReload(!reload);
    } catch (error) {
      console.error('Error customizing template:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to customize template',
        life: 3000
      });
    } finally {
      props.setGlobalLoader(false);
    }
  };

  const editTemplate = async (template: any) => {
    if (!template?.id) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Invalid template selected'
      });
      return;
    }

    try {
      console.log('Fetching template for editing, ID:', template.id);
      const templateData = await getChecklistTemplate(template.id);
      console.log('Template data received for editing:', templateData);
      
      if (!templateData || !templateData.id) {
        console.error('Invalid template data received for editing:', templateData);
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Template data is incomplete or invalid'
        });
        return;
      }
      
      // getChecklistTemplate returns the template directly, not wrapped in content
      setSelectedTemplate(templateData);
      setDialogVisible(true);
      console.log('Edit dialog opened with template:', templateData.name);
    } catch (error) {
      console.error('Error fetching template details:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load template details'
      });
    }
  };


  const confirmDeleteTemplate = (template: any) => {
    if (!template?.id || !template?.name) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Invalid template selected'
      });
      return;
    }

    confirmDialog({
      message: `Are you sure you want to delete the template "${template.name}"? This action cannot be undone.`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      accept: () => deleteTemplateConfirmed(template)
    });
  };

  const deleteTemplateConfirmed = async (template: any) => {
    if (!template?.id) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Invalid template selected'
      });
      return;
    }

    try {
      await deleteChecklistTemplate(template.id);
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: `Template "${template.name}" deleted successfully`
      });
      setReload(!reload);
    } catch (error: any) {
      console.error('Error deleting template:', error);
      
      let errorMessage = 'Failed to delete template';
      
      if (error?.response?.status === 403) {
        errorMessage = 'You do not have permission to delete this template';
      } else if (error?.response?.status === 404) {
        errorMessage = 'Template not found or already deleted';
      } else if (error?.response?.status === 409) {
        errorMessage = 'Cannot delete template - it may be in use by active checklists';
      } else if (error?.message) {
        errorMessage = error.message;
      }

      toast.current?.show({
        severity: 'error',
        summary: 'Delete Failed',
        detail: errorMessage
      });
    }
  };

  const duplicateTemplate = async (template: any) => {
    if (!template?.id || !template?.name) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Invalid template selected'
      });
      return;
    }

    try {
      props.setGlobalLoader(true);
      
      await duplicateChecklistTemplate(template.id);
      
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: `Template "${template.name}" duplicated successfully`
      });
      
      setReload(!reload);
    } catch (error: any) {
      console.error('Error duplicating template:', error);
      
      let errorMessage = 'Failed to duplicate template';
      
      if (error?.message) {
        errorMessage = error.message;
      }

      toast.current?.show({
        severity: 'error',
        summary: 'Duplicate Failed',
        detail: errorMessage
      });
    } finally {
      props.setGlobalLoader(false);
    }
  };

  const contextItems = (): MenuItem[] => {
    if (!selectedTemplate) return [];
    
    const items: MenuItem[] = [];
    
    // In client context (or actual client user), show appropriate options
    if (shouldShowClientView) {
      if (selectedTemplate.template_type === 'master' && !selectedTemplate.is_customized) {
        // Master template not yet customized - show Customize option
        items.push({
          label: 'Customize',
          icon: 'pi pi-copy',
          command: () => customizeTemplate(selectedTemplate)
        });
      } else if (selectedTemplate.template_type === 'customized') {
        // Client's customized template - show Edit option
        items.push({
          label: 'Edit',
          icon: 'pi pi-pencil',
          command: () => editTemplate(selectedTemplate)
        });
      } else {
        // Master template already customized - show View option
        items.push({
          label: 'View',
          icon: 'pi pi-eye',
          command: () => viewTemplate(selectedTemplate)
        });
      }
      
  // (Removed deprecated "Test" action)
    } else {
      // For root context, show all options for master templates
      items.push(
        {
          label: 'View',
          icon: 'pi pi-eye',
          command: () => viewTemplate(selectedTemplate)
        },
        {
          label: 'Edit',
          icon: 'pi pi-pencil',
          command: () => editTemplate(selectedTemplate)
        },
        {
          label: 'Duplicate',
          icon: 'pi pi-copy',
          command: () => duplicateTemplate(selectedTemplate)
        },
        {
          separator: true
        },
        {
          label: 'Delete',
          icon: 'pi pi-trash',
          command: () => confirmDeleteTemplate(selectedTemplate)
        }
      );
      
  // (Removed deprecated "Test" action)
    }
    
    return items;
  };

  const onDialogHide = () => {
    setDialogVisible(false);
    setSelectedTemplate(null);
  };

  const onViewDialogHide = () => {
    console.log('View dialog hiding');
    setViewDialogVisible(false);
    setIsViewLoading(false); // Reset loading state when dialog closes
    
    // Clear selection state to prevent stale row data issues
    // This prevents the "row.value is null" error on subsequent clicks
    setTimeout(() => {
      setTemplateToView(null);
    }, 100);
  };

  const onTemplateSaved = () => {
    setReload(!reload);
  };

  return (
    <React.Fragment>
      <Box
        sx={{
          display: 'flex',
          mb: 1,
          gap: 1,
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'start', sm: 'center' },
          flexWrap: 'wrap',
          justifyContent: 'space-between',
        }}
      >
        <Typography level="h2" component="h1">
          {isClientContext ? 'Client Checklist Templates' : 'Checklist Templates'}
        </Typography>
      </Box>

      <GenericTableView 
        reload={reload} 
        baseFilters={baseFilter} 
        url={tableUrl}
        columns={columns} 
        contextItems={contextItems} 
        addButton={canCreateTemplates ? addButton : undefined}
        itemSelect={itemSelected} 
        contextItemSelected={contextItemSelected} 
      />

      <Dialog 
        header={selectedTemplate?.id ? 'Update Checklist Template' : 'Add Checklist Template'} 
        visible={dialogVisible} 
        style={{ width: '90vw', maxWidth: '1200px' }} 
        onHide={onDialogHide}
        draggable={false}
        resizable={false}
        maximizable
      >
        <ChecklistTemplateCreateDialog
          visible={true}
          onHide={onDialogHide}
          template={selectedTemplate}
          onSave={onTemplateSaved}
          session={props.session}
          isClientContext={isClientContext}
        />
      </Dialog>

      <ChecklistTemplateViewDialog
        visible={viewDialogVisible}
        onHide={onViewDialogHide}
        template={templateToView}
      />

      <ConfirmDialog />
      <Toast
        ref={toast}
        appendTo={typeof document !== 'undefined' ? document.body : undefined}
        style={{ zIndex: 2000 }}
      />
    </React.Fragment>
  );
}
